/**
 * 임베드 도면을 주차장 템플릿(public/templates/parking.json) 기준으로 보정한다.
 *
 * 1) 디바이스 아이콘(CCTV 등) 엑박 보정 — fixDeviceIcons
 *    저장된 graphJson 에 구워진 xlinkHref 가 시뮬레이터 컨텍스트에서 안 풀리는 경우
 *    (커스텀/백엔드 URL, 상대경로 등), 템플릿 공개 에셋(/assets/parking/*.svg)으로 교체.
 *    이미 유효한 로컬 에셋(`/assets/...`)이나 Data URI 아이콘은 그대로 보존.
 *
 * 2) 조명 프레임 stroke-only 보정 — normalizeLightingFrames
 *    템플릿상 조명 프레임(lightingFrame)은 `fillOpacity: 0` 즉 "선만" 보여야 하지만,
 *    에디터의 폴리곤 생성 경로가 모든 폴리곤에 `fillOpacity: 0.8` 을 구워넣어 색이
 *    채워진 채로 저장된다. 렌더 시 조명 프레임 폴리곤만 골라 fill 을 제거(선만)한다.
 *
 * 3) CCTV·주차구역 라벨만 남기기 — keepOnlyCctvAndZoneLabels
 *    맵 위 텍스트 라벨(JointJS `attrs.label.text`)을 CCTV 와 주차구역(zone) 의
 *    "이름" 2종류로만 제한한다. 그 외 셀(개별 주차면/동/조명/출입구/안내판/
 *    독립 텍스트 라벨 등)의 라벨 텍스트는 ''(빈 문자열)로 지워 표시하지 않는다.
 */

import { routeLayerToCategory } from '../../map-editor-shared/lib/layerUtils'

/** 타입/레이어/id 키워드 → 주차장 템플릿 공개 에셋. 위에서부터 먼저 매칭. */
const ASSET_RULES: { match: RegExp; url: string }[] = [
  { match: /warning.?cctv/, url: '/assets/parking/warningCctv.svg' },
  { match: /cctv|reader|videocam/, url: '/assets/parking/lightCctv.svg' },
  { match: /charger|charging/, url: '/assets/parking/charger.svg' },
  { match: /elevator/, url: '/assets/parking/elevator.svg' },
  { match: /bell/, url: '/assets/parking/bell.svg' },
  { match: /sign|guide|board/, url: '/assets/parking/sign.svg' },
]

/**
 * public/assets/parking/ 에 실제 존재하는 아이콘 basename(확장자 제외).
 * 저장된 graphJson 에는 레거시로 없는 파일(예: cctv.svg)을 가리키는 셀이 있어
 * 404 로 안 보였다 — `/assets/` 로 시작한다고 무조건 정상 취급하면 안 된다.
 */
const KNOWN_ASSET_BASENAMES = new Set([
  'bell',
  'charger',
  'charging',
  'elevator',
  'lightCctv',
  'parking',
  'sign',
  'warningCctv',
])

/**
 * 이 컨텍스트에서 그대로 로드 가능한 href 인지.
 * - data: URI 는 항상 OK
 * - /assets/ 경로는 basename 이 실제 존재하는 에셋일 때만 OK (없는 파일이면
 *   fixDeviceIcons 가 layer/type 키워드로 올바른 아이콘에 재매핑하도록 false)
 */
function isResolvableHref(href: unknown): href is string {
  if (typeof href !== 'string') return false
  if (href.startsWith('data:')) return true
  if (href.startsWith('/assets/')) {
    const base = (href.split('/').pop() ?? '').replace(/\.svg$/i, '')
    return KNOWN_ASSET_BASENAMES.has(base)
  }
  return false
}

function resolveAsset(key: string): string | null {
  for (const rule of ASSET_RULES) {
    if (rule.match.test(key)) return rule.url
  }
  return null
}

interface CellBody {
  fill?: unknown
  fillOpacity?: unknown
  stroke?: unknown
  strokeWidth?: unknown
  refPoints?: unknown
}

interface GraphCell {
  id?: unknown
  type?: unknown
  objectType?: unknown
  objectTypeId?: unknown
  position?: { x?: unknown; y?: unknown }
  size?: { width?: unknown; height?: unknown }
  data?: {
    objectType?: unknown
    objectType_?: unknown
    typeId?: unknown
    type?: unknown
    layer?: unknown
    kind?: unknown
    properties?: { type?: unknown; kind?: unknown; name?: unknown; [k: string]: unknown }
  }
  attrs?: {
    image?: { xlinkHref?: unknown }
    body?: CellBody
    label?: { text?: unknown }
  }
  [k: string]: unknown
}

/**
 * graphJson 의 깨진 디바이스 아이콘 href 를 주차장 템플릿 에셋으로 교체한 새 객체를 반환.
 * 변경이 없으면 원본을 그대로 반환(불필요한 리렌더 방지).
 */
export function fixDeviceIcons<T extends { cells?: GraphCell[] } | null | undefined>(graphJson: T): T {
  if (!graphJson || !Array.isArray(graphJson.cells)) return graphJson

  let changed = false
  const cells = graphJson.cells.map((cell) => {
    const image = cell?.attrs?.image
    if (!image || isResolvableHref(image.xlinkHref)) return cell

    const key = [
      cell.objectType,
      cell.data?.objectType,
      cell.data?.type,
      cell.data?.layer,
      cell.id,
      image.xlinkHref,
    ]
      .filter((v) => typeof v === 'string' && v)
      .join(' ')
      .toLowerCase()

    const url = resolveAsset(key)
    if (!url) return cell

    changed = true
    return { ...cell, attrs: { ...cell.attrs, image: { ...image, xlinkHref: url } } }
  })

  return changed ? { ...graphJson, cells } : graphJson
}

/**
 * 조명 프레임으로 식별되는 타입/레이어 키워드.
 * - 'lightingframe' / 'lighting' / '조명' : 템플릿 displayName·typeId 계열
 * - 'l-light' / 'c-light' : DXF/CSV import 시 부여되는 convention layer
 * 'lightCctv'(조명 CCTV) 는 아이콘(attrs.image)·point 라서 아래 폴리곤 게이트에서 제외됨.
 */
const LIGHTING_PATTERN = /lightingframe|lighting|조명|[-_]light(?:[-_]|$)/

/** body 에 면(fill)을 가질 수 있는 폴리곤 셀인지 (조명 점/아이콘은 제외). */
function isFillablePolygon(cell: GraphCell): boolean {
  if (cell?.attrs?.image) return false
  const type = typeof cell?.type === 'string' ? cell.type : ''
  if (/polygon|path/i.test(type)) return true
  return typeof cell?.attrs?.body?.refPoints === 'string'
}

/** 셀이 현재 채워져(fill) 보이는지 — 'none'/투명/0 opacity 면 이미 선만이라 건드릴 필요 없음. */
function hasVisibleFill(body: CellBody): boolean {
  const fill = body.fill
  if (typeof fill === 'string' && (fill === 'none' || fill === 'transparent')) return false
  if (body.fillOpacity === 0) return false
  return typeof fill === 'string' && fill.length > 0
}

/**
 * 조명 프레임 폴리곤을 템플릿 기준(`fillOpacity: 0`, stroke-only)으로 보정한 새 객체 반환.
 * fill 만 제거하고 stroke(선 색/두께)는 보존 — 선이 사라지지 않게 stroke 가 없으면
 * 원래 fill 색으로 대체한다. 변경이 없으면 원본을 그대로 반환.
 */
export function normalizeLightingFrames<T extends { cells?: GraphCell[] } | null | undefined>(
  graphJson: T,
): T {
  if (!graphJson || !Array.isArray(graphJson.cells)) return graphJson

  let changed = false
  const cells = graphJson.cells.map((cell) => {
    if (!isFillablePolygon(cell)) return cell
    const body = cell.attrs?.body
    if (!body || !hasVisibleFill(body)) return cell

    const key = [
      cell.objectType,
      cell.objectTypeId,
      cell.data?.objectType,
      cell.data?.typeId,
      cell.data?.type,
      cell.data?.layer,
      cell.data?.properties?.type,
    ]
      .filter((v) => typeof v === 'string' && v)
      .join(' ')
      .toLowerCase()

    if (!LIGHTING_PATTERN.test(key)) return cell

    // fill 제거(선만), stroke 는 보존하되 없으면 원래 fill 색으로 폴백.
    const strokeColor =
      typeof body.stroke === 'string' && body.stroke && body.stroke !== 'none'
        ? body.stroke
        : (body.fill as string)
    changed = true
    return {
      ...cell,
      attrs: {
        ...cell.attrs,
        body: {
          ...body,
          fill: 'none',
          fillOpacity: 0,
          stroke: strokeColor,
          strokeWidth: typeof body.strokeWidth === 'number' ? body.strokeWidth : 2,
        },
      },
    }
  })

  return changed ? { ...graphJson, cells } : graphJson
}

/**
 * CCTV 이름을 맵 라벨로 노출한다.
 *
 * CCTV(조명/단방향) 는 아이콘 객체라 에디터 배치 시 `attrs.label.text` 가 ''(빈 문자열)로
 * 저장되고 이름은 `data.properties.name` 에만 들어간다(useObjectCreation: "No auto-naming").
 * 그래서 시뮬레이터 임베드에서 CCTV 이름이 전혀 안 보였다 — keepOnlyCctvAndZoneLabels 는
 * 기존 라벨을 보존/제거만 할 뿐 새로 만들지 못하기 때문.
 *
 * CCTV 셀에 한해, 라벨 텍스트가 비어있고 properties.name 이 있으면 그 이름을 라벨 텍스트로
 * 채운다. 이미 인라인 라벨이 있으면 보존. 그 외 셀은 손대지 않는다. 변경이 없으면 원본을
 * 그대로 반환(불필요한 리렌더 방지). keepOnlyCctvAndZoneLabels 보다 먼저 적용하면
 * 주입된 CCTV 라벨이 보존된다. 시뮬레이터 임베드 경로 전용 보정.
 */
export function addCctvNameLabels<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): T {
  if (!graphJson || !Array.isArray(graphJson.cells)) return graphJson

  let changed = false
  const cells = graphJson.cells.map((cell) => {
    if (!isCctvCell(cell)) return cell
    if (hasLabelText(cell)) return cell // 인라인 명명이 있으면 유지
    const name = cell.data?.properties?.name
    if (typeof name !== 'string' || !name) return cell

    changed = true
    const label = (cell.attrs?.label as Record<string, unknown>) || {}
    return {
      ...cell,
      attrs: {
        ...cell.attrs,
        label: {
          ...label,
          text: name,
          // 어두운 도면에서 보이도록 fill 보장(기존 fill 이 있으면 보존).
          fill: typeof label.fill === 'string' && label.fill ? label.fill : '#ffffff',
        },
      },
    }
  })

  return changed ? { ...graphJson, cells } : graphJson
}

/** 비상벨 셀 판정 — extractMapBells 와 동일 규칙 (layer/type 에 emergency|bell). */
function isBellCellRaw(cell: GraphCell): boolean {
  const layer = typeof cell.data?.layer === 'string' ? cell.data.layer : ''
  const type = typeof cell.data?.type === 'string' ? cell.data.type : ''
  return /emergency|bell/i.test(layer) || /emergency|bell/i.test(type)
}

/**
 * 비상벨 표시용 짧은 이름 — 이름 끝의 숫자만 (예: "비상벨-P1_B2_07" → "07").
 * 숫자가 없으면 원래 이름 그대로. 맵 라벨이 길면 마커를 가리므로 숫자만 쓴다.
 */
export function bellShortName(name: string): string {
  const m = /(\d+)\s*$/.exec(name)
  return m ? m[1] : name
}

/**
 * 비상벨 이름을 맵 라벨로 노출한다 (addCctvNameLabels 의 비상벨 버전).
 * 벨 셀은 아이콘 객체라 라벨이 비어 있고 이름은 properties.name 에만 있다.
 * 라벨 텍스트는 짧은 이름(끝 숫자)으로, 어두운 도면에서 보이도록 흰색으로 채운다.
 * 시뮬레이터 임베드 경로 전용 보정.
 */
export function addBellNameLabels<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): T {
  if (!graphJson || !Array.isArray(graphJson.cells)) return graphJson

  let changed = false
  const cells = graphJson.cells.map((cell) => {
    if (!isBellCellRaw(cell)) return cell
    if (hasLabelText(cell)) return cell // 인라인 명명이 있으면 유지
    const name = cell.data?.properties?.name
    const raw = typeof name === 'string' && name ? name : cell.id != null ? String(cell.id) : ''
    if (!raw) return cell

    changed = true
    const label = (cell.attrs?.label as Record<string, unknown>) || {}
    return {
      ...cell,
      attrs: {
        ...cell.attrs,
        label: { ...label, text: bellShortName(raw), fill: '#ffffff' },
      },
    }
  })

  return changed ? { ...graphJson, cells } : graphJson
}

export interface CctvParkingLink {
  /** lights cctvName (data.properties.name). */
  cctvName: string;
  /** 이 CCTV 가 담당하는 주차면들 (id + 표시 이름). */
  parking: { id: string; name: string }[];
}

/**
 * CCTV ↔ 주차면(parkingLocations) 1:n 관계를 맵에서 추출한다.
 * 관계는 CCTV 셀의 `data.properties.linkedParkingLocationIds`(주차면 cell id 배열)에
 * 저장돼 있다. 주차면 cell id → 표시 이름(properties.name)으로 매핑해 반환.
 * 만공차 체크에서 "CCTV 담당 주차면 점유 → 만공차등" 계산에 쓴다.
 */
export function extractCctvParkingLinks<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): CctvParkingLink[] {
  if (!graphJson || !Array.isArray(graphJson.cells)) return [];
  const cells = graphJson.cells;

  // 주차면 cell id → 표시 이름
  const parkingNameById = new Map<string, string>();
  for (const cell of cells) {
    const layer = cell.data?.layer;
    if (typeof layer !== 'string' || !/parking/i.test(layer)) continue;
    const id = cell.id != null ? String(cell.id) : '';
    if (!id) continue;
    const nm = cell.data?.properties?.name;
    parkingNameById.set(id, typeof nm === 'string' && nm ? nm : id);
  }

  const links: CctvParkingLink[] = [];
  for (const cell of cells) {
    if (!isCctvCell(cell)) continue;
    const propName = cell.data?.properties?.name;
    const labelText = (cell.attrs?.label as { text?: unknown } | undefined)?.text;
    const cctvName =
      typeof propName === 'string' && propName
        ? propName
        : typeof labelText === 'string'
          ? labelText
          : '';
    const ids = (cell.data?.properties as { linkedParkingLocationIds?: unknown })
      ?.linkedParkingLocationIds;
    if (!cctvName || !Array.isArray(ids) || ids.length === 0) continue;
    const parking = ids
      .filter((i): i is string => typeof i === 'string' && !!i)
      .map(id => ({ id, name: parkingNameById.get(id) ?? id }));
    if (parking.length > 0) links.push({ cctvName, parking });
  }
  return links;
}

/** CCTV 이름 → 연결 조명 셀 id 목록. */
export interface CctvLightLink {
  cctvName: string;
  lightIds: string[];
}

/** 셀 중심 좌표 (position + size/2). 좌표가 없으면 null. */
function cellCenter(cell: GraphCell): { x: number; y: number } | null {
  const px = Number(cell.position?.x);
  const py = Number(cell.position?.y);
  if (!Number.isFinite(px) || !Number.isFinite(py)) return null;
  const w = Number(cell.size?.width) || 0;
  const h = Number(cell.size?.height) || 0;
  return { x: px + w / 2, y: py + h / 2 };
}

/**
 * CCTV ↔ 조명(light 카테고리 셀) 관계를 맵에서 추출한다.
 * 비상벨 호출 시 "연결된 CCTV 의 조명 점멸" 시각화에 쓴다.
 *
 * 1) 에디터 관계가 저장돼 있으면 그대로 사용: CCTV 셀 properties 의
 *    linked*Ids 배열 중 조명 셀 id 를 가리키는 항목.
 * 2) 관계가 하나도 없으면(현장 맵 대부분) 물리 배치 기준 폴백:
 *    각 조명 셀을 가장 가까운 CCTV 에 귀속시킨다 — 조명 CCTV 가 자기 주변
 *    조명을 제어하는 실제 구성과 일치한다.
 */
export function extractCctvLightLinks<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): CctvLightLink[] {
  if (!graphJson || !Array.isArray(graphJson.cells)) return [];
  const cells = graphJson.cells;

  const lightCells = cells.filter(c => cellCategory(c) === 'light' && c.id != null);
  if (lightCells.length === 0) return [];
  const lightIdSet = new Set(lightCells.map(c => String(c.id)));

  const cctvCells = cells.filter(c => isCctvCell(c));
  const nameOf = (c: GraphCell): string => {
    const n = c.data?.properties?.name;
    return typeof n === 'string' && n ? n : String(c.id ?? '');
  };

  // 1) 저장된 관계 우선
  const linked: CctvLightLink[] = [];
  for (const cctv of cctvCells) {
    const props = cctv.data?.properties;
    if (!props) continue;
    const ids: string[] = [];
    for (const [key, value] of Object.entries(props)) {
      if (!/^linked.*Ids$/.test(key) || !Array.isArray(value)) continue;
      for (const id of value) {
        if (typeof id === 'string' && lightIdSet.has(id)) ids.push(id);
      }
    }
    if (ids.length > 0) linked.push({ cctvName: nameOf(cctv), lightIds: ids });
  }
  if (linked.length > 0) return linked;

  // 2) 폴백: 조명별 최근접 CCTV 파티셔닝
  const cctvCenters = cctvCells
    .map(c => ({ name: nameOf(c), center: cellCenter(c) }))
    .filter((c): c is { name: string; center: { x: number; y: number } } => !!c.center);
  if (cctvCenters.length === 0) return [];

  const byCctv = new Map<string, string[]>();
  for (const light of lightCells) {
    const lc = cellCenter(light);
    if (!lc) continue;
    let best: string | null = null;
    let bestDist = Infinity;
    for (const { name, center } of cctvCenters) {
      const d = (center.x - lc.x) ** 2 + (center.y - lc.y) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = name;
      }
    }
    if (best) {
      const arr = byCctv.get(best) ?? [];
      arr.push(String(light.id));
      byCctv.set(best, arr);
    }
  }
  return Array.from(byCctv.entries()).map(([cctvName, lightIds]) => ({ cctvName, lightIds }));
}

/** 맵 graphJson 에서 추출한 비상벨 — SimulatorEmergencyBell 호환 형태. */
export interface MapBell {
  id: string;
  name: string;
  /** 이 벨과 연결된 CCTV 이름 (관계 저장 없으면 최근접 CCTV). */
  linked_cctv_id: string;
  x: number;
  y: number;
}

/**
 * 맵 graphJson 에서 비상벨 목록을 추출한다 — 시뮬레이터 비상벨 API 가
 * (export 형식 map_data 가 아니라서) 빈 목록을 줄 때의 폴백.
 *
 * 벨→CCTV 연결 해석 순서:
 *   1. 벨 properties 의 linkedCctvId / linked*Id 가 CCTV 셀 id 를 가리키면 그 CCTV
 *   2. CCTV properties 의 linked*Ids 가 벨 id 를 담고 있으면 그 CCTV (역방향)
 *   3. 최근접 CCTV — 현장에서 비상벨은 조명 CCTV 기둥에 부착되므로 물리 배치와 일치
 */
export function extractMapBells<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): MapBell[] {
  if (!graphJson || !Array.isArray(graphJson.cells)) return [];
  const cells = graphJson.cells;

  const isBellCell = (c: GraphCell): boolean => {
    const layer = typeof c.data?.layer === 'string' ? c.data.layer : '';
    const type = typeof c.data?.type === 'string' ? c.data.type : '';
    return /emergency|bell/i.test(layer) || /emergency|bell/i.test(type);
  };
  const bellCells = cells.filter(c => isBellCell(c) && c.id != null);
  if (bellCells.length === 0) return [];

  const cctvCells = cells.filter(c => isCctvCell(c) && c.id != null);
  const cctvNameOf = (c: GraphCell): string => {
    const n = c.data?.properties?.name;
    return typeof n === 'string' && n ? n : String(c.id);
  };
  const cctvById = new Map(cctvCells.map(c => [String(c.id), c]));

  // 2) 역방향: CCTV linked*Ids 에 벨 id 가 들어 있는 경우
  const cctvNameByBellId = new Map<string, string>();
  for (const cctv of cctvCells) {
    const props = cctv.data?.properties;
    if (!props) continue;
    for (const [key, value] of Object.entries(props)) {
      if (!/^linked.*Ids$/.test(key) || !Array.isArray(value)) continue;
      for (const id of value) {
        if (typeof id === 'string' && !cctvNameByBellId.has(id)) {
          cctvNameByBellId.set(id, cctvNameOf(cctv));
        }
      }
    }
  }

  const cctvCenters = cctvCells
    .map(c => ({ name: cctvNameOf(c), center: cellCenter(c) }))
    .filter((c): c is { name: string; center: { x: number; y: number } } => !!c.center);

  const out: MapBell[] = [];
  for (const bell of bellCells) {
    const id = String(bell.id);
    const props = bell.data?.properties;
    const nm = props?.name;
    const name = typeof nm === 'string' && nm ? nm : id;
    const center = cellCenter(bell);

    // 1) 벨 쪽 linked*Id 가 CCTV 셀을 가리키는 경우
    let linked = '';
    if (props) {
      for (const [key, value] of Object.entries(props)) {
        if (!/^linked.*Id$/.test(key) || typeof value !== 'string' || !value) continue;
        const target = cctvById.get(value);
        if (target) {
          linked = cctvNameOf(target);
          break;
        }
      }
    }
    // 2) 역방향
    if (!linked) linked = cctvNameByBellId.get(id) ?? '';
    // 3) 최근접 CCTV
    if (!linked && center && cctvCenters.length > 0) {
      let bestDist = Infinity;
      for (const { name: cn, center: cc } of cctvCenters) {
        const d = (cc.x - center.x) ** 2 + (cc.y - center.y) ** 2;
        if (d < bestDist) {
          bestDist = d;
          linked = cn;
        }
      }
    }

    out.push({ id, name, linked_cctv_id: linked, x: center?.x ?? 0, y: center?.y ?? 0 });
  }
  return out;
}

/** layer/type 문자열 → 정규화 카테고리. data.layer 우선, 없으면 data.type 폴백
 *  (zoneCctvRelations.elementCategory / buildMapData 와 동일 규칙). */
function cellCategory(cell: GraphCell): string {
  const layer = cell.data?.layer
  if (typeof layer === 'string' && layer) {
    const c = routeLayerToCategory(layer)
    if (c !== 'unknown') return c
  }
  const type = cell.data?.type
  if (typeof type === 'string' && type) {
    return routeLayerToCategory(type)
  }
  return 'unknown'
}

/** 조명/단방향 CCTV 둘 다 포함 (isCctvElement 와 동일 판정, raw cell 버전). */
function isCctvCell(cell: GraphCell): boolean {
  const c = cellCategory(cell)
  return c === 'cctv' || c === 'warningCctv'
}

/**
 * graphJson 에 실제 배치된 CCTV 들의 이름 목록(중복 제거, 등장 순서 보존).
 * 시뮬레이터 CCTV 목록 API 가 비어 있어도 맵의 CCTV 로 선택/입력할 수 있도록,
 * properties.name → label.text 순으로 이름을 모은다.
 */
export function extractCctvNames<T extends { cells?: GraphCell[] } | null | undefined>(
  graphJson: T,
): string[] {
  if (!graphJson || !Array.isArray(graphJson.cells)) return []
  const seen = new Set<string>()
  const names: string[] = []
  for (const cell of graphJson.cells) {
    if (!isCctvCell(cell)) continue
    const propName = cell.data?.properties?.name
    const labelText = (cell.attrs?.label as { text?: unknown } | undefined)?.text
    const name =
      typeof propName === 'string' && propName
        ? propName
        : typeof labelText === 'string' && labelText
          ? labelText
          : ''
    if (name && !seen.has(name)) {
      seen.add(name)
      names.push(name)
    }
  }
  return names
}

/** 맵 CCTV 한 개 — 이름 + IP(properties.ipAddress, 없으면 빈 문자열) + 종류. */
export interface MapCctv {
  name: string;
  ip: string;
  /** 'cctv'=조명(전방위), 'warningCctv'=단방향(경광등/충돌방지등 대상). */
  kind: 'cctv' | 'warningCctv';
}

/**
 * graphJson 의 CCTV 들을 이름·IP·종류와 함께 추출한다(중복 이름 제거, 등장 순서 보존).
 * 비상벨 탭에서 "이름 (IP)" 다중 선택지로 쓰며, IP 는 도면 추출로 주입된
 * properties.ipAddress 를 사용한다. kind 는 조명 탭 자동 실행이 단방향(경광등)
 * CCTV 인지 판별해 좌우 점등 기준을 다르게 적용하는 데 쓴다.
 */
export function extractCctvList<T extends { cells?: GraphCell[] } | null | undefined>(
  graphJson: T,
): MapCctv[] {
  if (!graphJson || !Array.isArray(graphJson.cells)) return []
  const seen = new Set<string>()
  const out: MapCctv[] = []
  for (const cell of graphJson.cells) {
    if (!isCctvCell(cell)) continue
    const propName = cell.data?.properties?.name
    const labelText = (cell.attrs?.label as { text?: unknown } | undefined)?.text
    const name =
      typeof propName === 'string' && propName
        ? propName
        : typeof labelText === 'string' && labelText
          ? labelText
          : ''
    if (!name || seen.has(name)) continue
    const ipRaw = (cell.data?.properties as { ipAddress?: unknown } | undefined)?.ipAddress
    const ip = typeof ipRaw === 'string' ? ipRaw : ''
    const kind = cellCategory(cell) === 'warningCctv' ? 'warningCctv' : 'cctv'
    seen.add(name)
    out.push({ name, ip, kind })
  }
  return out
}

/**
 * 주차구역(zone) 셀인지. 정의적 마커는 `data.properties.kind === 'zone'`
 * (findZoneElements 와 동일). 레거시/대체 표기를 위해 layer/type 카테고리가
 * 'zone' 인 경우도 포함. 주의: '주차 구역'(displayName) 은 parkingLocation 으로
 * 라우팅되는 개별 주차면이라 zone 이 아니다 — routeLayerToCategory 가 구분한다. */
function isZoneCell(cell: GraphCell): boolean {
  if (cell.data?.properties?.kind === 'zone') return true
  return cellCategory(cell) === 'zone'
}

/** 셀이 보이는 텍스트 라벨(attrs.label.text)을 가지고 있는지. */
function hasLabelText(cell: GraphCell): boolean {
  const text = cell.attrs?.label?.text
  return typeof text === 'string' && text.length > 0
}

/**
 * 맵 위 텍스트 라벨을 CCTV·주차구역(zone)·비상벨의 이름 3종류로만 제한한 새 객체 반환.
 *
 * JointJS 는 셀의 `attrs.label.text` 를 텍스트로 렌더하며, 맵의 모든 가시 라벨은
 * 이 attr 하나로 표현된다(요소 셀의 인라인 라벨 + `kind: 'label'` 독립 텍스트 셀
 * 모두 동일). 따라서 유지 대상이 아닌 셀의 `attrs.label.text` 를 ''(빈 문자열)로
 * 지우면 그 라벨만 화면에서 사라진다(셀 자체/도형은 그대로 유지).
 *
 * 유지되는 라벨은 글자색을 흰색으로 강제한다 — 에디터에서 저장된 라벨 fill 이
 * 검정인 맵이 있어 어두운 도면 배경에서 이름이 전혀 안 보였다. 변경이 없으면
 * 원본을 그대로 반환(불필요한 리렌더 방지). 시뮬레이터 임베드 경로 전용 보정.
 */
export function keepOnlyCctvAndZoneLabels<
  T extends { cells?: GraphCell[] } | null | undefined,
>(graphJson: T): T {
  if (!graphJson || !Array.isArray(graphJson.cells)) return graphJson

  let changed = false
  const cells = graphJson.cells.map((cell) => {
    if (!hasLabelText(cell)) return cell
    if (isCctvCell(cell) || isZoneCell(cell) || isBellCellRaw(cell)) {
      // 유지 라벨 — 검정/미지정 fill 을 흰색으로 강제해 어두운 배경에서 보이게.
      // 이미 흰색이면 원본 유지(불필요한 리렌더 방지).
      const label = cell.attrs?.label as Record<string, unknown>
      const fill = typeof label.fill === 'string' ? label.fill.toLowerCase() : ''
      if (fill === '#ffffff' || fill === '#fff' || fill === 'white') return cell
      changed = true
      return {
        ...cell,
        attrs: { ...cell.attrs, label: { ...label, fill: '#ffffff' } },
      }
    }

    changed = true
    return {
      ...cell,
      attrs: { ...cell.attrs, label: { ...cell.attrs?.label, text: '' } },
    }
  })

  return changed ? { ...graphJson, cells } : graphJson
}
